import moment from 'moment';

export const DEFAULT_DATE_FORMAT = 'D MMM YY'; // 1 Jan 24
export const YYYY_MM_DD = 'YYYY-MM-DD'; // 2014-05-15
export const YYYY_MM_DD_HH_MM_SS = 'YYYY-MM-DD HH:mm:ss'; // 2014-05-15 00:00:00
export const DD_MM_YYYY = 'DD/MM/YYYY'; // 15/05/2014
export const DD_MMM_YYYY = 'DD MMM YYYY'; // 15 May 2014
export const MMM_DD_YYYY = 'MMM DD YYYY'; // May 15 2014
export const D_MMM_YYYY = 'D MMM YYYY'; // 1 May 2014
export const DD_MMM_YY = 'DD MMM YY'; // 15 May 14
export const D_MMM_YY = 'D MMM YY'; // 1 May 14
export const CHAT_CONVERSATION_STARTED_DISPLAY_FORMAT =
  'dddd D MMM YYYY, h:mmA'; // Thursday 6 Aug 2026, 9:56AM
export const DATE_PARSE_FORMATS = [
  moment.ISO_8601,
  YYYY_MM_DD_HH_MM_SS,
  YYYY_MM_DD,
  DD_MM_YYYY,
  DD_MMM_YYYY,
  D_MMM_YYYY,
  DEFAULT_DATE_FORMAT,
  DD_MMM_YY,
  D_MMM_YY,
];
export const isTodayDateString = (isoDateString: string) => {
  return moment(isoDateString).isSame(new Date(), 'day');
};

export const isAfterTodayDateString = (isoDateString: string) => {
  return moment(isoDateString).isAfter(new Date(), 'day');
};

export const isBeforeTodayDateString = (isoDateString: string) => {
  return moment(isoDateString).isBefore(new Date(), 'day');
};

export const isTodayOrAfterTodayDateString = (isoDateString: string) => {
  return (
    isAfterTodayDateString(isoDateString) || isTodayDateString(isoDateString)
  );
};

export const isTodayOrBeforeTodayDateString = (isoDateString: string) => {
  return (
    isBeforeTodayDateString(isoDateString) || isTodayDateString(isoDateString)
  );
};

export const isValidDate = (date: string | Date) => {
  if (!date) {
    return false;
  }
  return moment(date).isValid();
};

export const convertToFormattedDateString = (
  dateString: string,
  format: string = DEFAULT_DATE_FORMAT,
): string => {
  const parsed = moment.utc(
    (dateString ?? '').trim(),
    DATE_PARSE_FORMATS,
    true,
  );
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.format(format);
};

export const formatChatConversationStartedAt = (
  value: string | null | undefined,
  outputFormat: string = CHAT_CONVERSATION_STARTED_DISPLAY_FORMAT,
): string => {
  if (!value) {
    return '';
  }

  const parsed = moment.utc(value, [moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return '';
  }

  return parsed.local().format(outputFormat);
};

export const compareDateString = (date1: string, date2: string) => {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    return 0;
  }
  return moment(date1).diff(moment(date2));
};

export const calculateAge = (
  dateString: string,
): { years: number; months: number; weeks: number } => {
  const today = moment.utc();
  const birthDate = moment.utc(
    (dateString ?? '').trim(),
    DATE_PARSE_FORMATS,
    true,
  );
  if (!birthDate.isValid()) {
    return { years: 0, months: 0, weeks: 0 };
  }
  while (birthDate.isAfter(today, 'day')) {
    birthDate.subtract(100, 'years');
  }
  const years = today.diff(birthDate, 'years');
  birthDate.add(years, 'years');
  const months = today.diff(birthDate, 'months');
  birthDate.add(months, 'months');
  const weeks = today.diff(birthDate, 'weeks');

  return { years, months, weeks };
};

export const formatAge = (dob?: string | null): string => {
  if (!dob) {
    return '';
  }
  const { years, months, weeks } = calculateAge(dob);
  if (years >= 2) {
    return `${years}yrs`;
  }
  if (years > 0 || months >= 4) {
    return `${years * 12 + months}mths`;
  }
  return `${months * 4 + weeks}wks`;
};

export const convertDateInstanceToDateString = (
  date: Date,
  format: string = DEFAULT_DATE_FORMAT,
): string => {
  if (!isValidDate(date)) {
    return 'Invalid Date';
  }
  return moment(date).format(format);
};

export const convertDateStringToDateInstance = (dateString: string): Date => {
  if (!isValidDate(dateString)) {
    return new Date();
  }
  return new Date(dateString);
};

export const calculateDateGap = (date1: string, date2: string) => {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    return null;
  }
  const result = moment(date1).diff(moment(date2), 'days');
  if (typeof result === 'number') {
    return Math.abs(result) + ' days';
  }
  return null;
};

export const calculateTimeGap = (
  date1: string,
  date2: string,
  {
    hourFormat = 'hrs',
    daysFormat = 'days',
    monthsFormat = 'months',
  }: { hourFormat: string; daysFormat: string; monthsFormat: string },
) => {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    return null;
  }
  const result = moment(date1).diff(moment(date2), 'hours');
  if (typeof result === 'number') {
    if (result <= 0) {
      return '0' + hourFormat;
    }
    if (result <= 24) {
      return Math.floor(Math.abs(result)) + hourFormat;
    }
    if (result <= 24 * 30) {
      return Math.floor(Math.abs(result / 24)) + daysFormat;
    }
    return Math.floor(Math.abs(result / (24 * 30))) + monthsFormat;
  }
  return null;
};

export const getDaysBefore = (
  to: string,
  from: string = new Date().toISOString(),
  style: 'data-annotation' | 'patient-details' = 'patient-details',
): string => {
  if (!to || !isValidDate(to)) return '-';
  const fromDate = moment(from).startOf('day');
  const target = moment(to).startOf('day');

  const diff = fromDate.diff(target, 'days');
  if (style === 'data-annotation') {
    if (diff <= 0) return '0 days before';
    if (diff === 1) return '1 day before';
  } else if (style === 'patient-details') {
    if (diff <= 0) return 'Today';
    if (diff === 1) return 'Yesterday';
  }
  return `${diff} day${diff > 1 ? 's' : ''} before`;
};

export const getTodayDateString = (
  format: string = DEFAULT_DATE_FORMAT,
): string => {
  return moment().format(format);
};

/**
 * Formats a UTC timestamp to local time in DD/MM/YYYY hh:mm A format
 * @param timestamp - UTC timestamp string
 * @returns Formatted timestamp in local timezone with 12-hour format
 */
export const formatDataSourceTimestamp = (timestamp: string): string => {
  try {
    // Ensure the timestamp is treated as UTC
    // If it doesn't end with 'Z', add it to explicitly mark as UTC
    let utcTimestamp = timestamp;
    if (
      !timestamp.endsWith('Z') &&
      !timestamp.includes('+') &&
      !timestamp.includes('Z')
    ) {
      utcTimestamp = timestamp + 'Z';
    }

    // Parse the UTC timestamp and convert to local time
    const date = moment.utc(utcTimestamp).local();

    // Verify the date is valid
    if (!date.isValid()) {
      console.error('Invalid date:', timestamp);
      return timestamp;
    }

    // Format as "DD/MM/YYYY hh:mm A" (12-hour format with AM/PM)
    return date.format('DD/MM/YYYY hh:mm A');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return timestamp;
  }
};

export const APPOINTMENT_TIME_FORMAT = 'h:mma'; // 9:30am
export const APPOINTMENT_DATE_FORMAT = 'D/M/YY'; // 5/8/26

const APPOINTMENT_PARSE_FORMATS = [
  ...DATE_PARSE_FORMATS,
  'h:mm a',
  'h:mmA',
  'HH:mm',
  'D/M/YYYY',
  'D/M/YY',
  'DD/MM/YYYY',
  'DD/MM/YY',
];

/**
 * Combines separate appointment date and time strings and formats them as
 * "{time} on {date}", e.g. "9:30am on 5/8/26".
 *
 * Falls back to the raw input (`${time} on ${date}`) if parsing fails.
 */
export const formatAppointmentDateTime = (
  appointmentDate: string,
  appointmentTime: string,
): string => {
  const trimmedDate = (appointmentDate ?? '').trim();
  const trimmedTime = (appointmentTime ?? '').trim();

  if (!trimmedDate && !trimmedTime) {
    return '';
  }

  const combinedInput =
    trimmedDate && trimmedTime
      ? `${trimmedDate} ${trimmedTime}`
      : trimmedDate || trimmedTime;

  const parsedCombined = moment(combinedInput, APPOINTMENT_PARSE_FORMATS, true);

  if (parsedCombined.isValid()) {
    const formattedTime = parsedCombined.format(APPOINTMENT_TIME_FORMAT);
    const formattedDate = parsedCombined.format(APPOINTMENT_DATE_FORMAT);
    return `${formattedTime} on ${formattedDate}`;
  }

  const parsedDate = moment(trimmedDate, APPOINTMENT_PARSE_FORMATS, true);
  const parsedTime = moment(trimmedTime, APPOINTMENT_PARSE_FORMATS, true);

  const formattedTime = parsedTime.isValid()
    ? parsedTime.format(APPOINTMENT_TIME_FORMAT)
    : trimmedTime;
  const formattedDate = parsedDate.isValid()
    ? parsedDate.format(APPOINTMENT_DATE_FORMAT)
    : trimmedDate;

  if (formattedTime && formattedDate) {
    return `${formattedTime} on ${formattedDate}`;
  }
  return formattedTime || formattedDate || '';
};
